/* eslint-disable @atlassian/relay/must-use-inline */
/* eslint-disable @atlassian/relay/unused-fields */
import React, { useEffect, useCallback, useRef } from 'react';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';
import FeedSkeleton from '@jira/issue__feed-skeleton/src/view.tsx';
import secondsToTimeString from '@jira/issue__get-formatted-time-string/src/seconds-to-time-string/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { ActivitySortOrderType } from '@atlassian/jira-issue-shared-types/src/common/types/activity-sort-order-type.tsx';
import type {
	IssueHistoryFragment$key,
	IssueHistoryFragment$data,
} from '@atlassian/jira-relay/src/__generated__/IssueHistoryFragment.graphql';
import type { IssueHistoryQuery } from '@atlassian/jira-relay/src/__generated__/IssueHistoryQuery.graphql';

import { timeTrackingConfigurationSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/time-tracking-selector.tsx';
import { useSelector } from '@atlassian/jira-react-redux/src/index.tsx';
import { transformChangelogItem } from '../services/issue-history/history-item-generator/utils.tsx';
import { groupHistoryItems } from '../services/issue-history/utils.tsx';
import { FeedContainer } from '../styled.tsx';
import HistoryItemsView from './history-items/index.tsx';

type IssueKey = string;

// Type aliases for better type safety
type HistoryEdgeNode = NonNullable<
	NonNullable<NonNullable<IssueHistoryFragment$data['history']>['edges']>[number]
>['node'];

type HistoryEdge = NonNullable<NonNullable<IssueHistoryFragment$data['history']>['edges']>[number];

const HISTORY_ITEMS_TO_LOAD = 100;

type Props = {
	issueKey: IssueKey;
	sortOrder: ActivitySortOrderType;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	GroupedActivityEntryComponent?: React.ComponentType<Record<any, any>>;
	issueViewRelayFragment?: IssueHistoryFragment$key | null; // For MetaDataWrapper
	onHistoryFetchAnalytics: (error?: Error | null) => void;
};

const DEFAULT_QUERY_PAGINATION_CURSORS = {
	first: HISTORY_ITEMS_TO_LOAD,
	after: undefined,
	last: undefined,
	before: undefined,
};

// eslint-disable-next-line jira/relay/no-query
const IssueHistoryQuery = graphql`
	query IssueHistoryQuery(
		$issueKey: String!
		$cloudId: ID!
		$first: Int
		$after: String
		$last: Int
		$before: String
		$orderBy: String
	) {
		jira {
			issueByKey(key: $issueKey, cloudId: $cloudId) @required(action: THROW) {
				...IssueHistoryFragment
			}
		}
	}
`;

const IssueHistoryRelay = (props: Props): React.JSX.Element | null => {
	const { issueKey, sortOrder, GroupedActivityEntryComponent, onHistoryFetchAnalytics } = props;
	const cloudId = useCloudId();

	// Convert sort order to GraphQL orderBy parameter
	const orderBy = sortOrder === 'OLDEST_FIRST' ? 'created' : '-created';
	const timeTrackingConfig = useSelector(timeTrackingConfigurationSelector);

	const queryData = useLazyLoadQuery<IssueHistoryQuery>(
		IssueHistoryQuery,
		{
			issueKey,
			cloudId,
			orderBy,
			first: DEFAULT_QUERY_PAGINATION_CURSORS.first,
			after: DEFAULT_QUERY_PAGINATION_CURSORS.after,
			last: DEFAULT_QUERY_PAGINATION_CURSORS.last,
			before: DEFAULT_QUERY_PAGINATION_CURSORS.before,
		},
		{
			fetchPolicy: 'store-and-network',
		},
	);

	const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
		IssueHistoryQuery,
		IssueHistoryFragment$key
	>(
		graphql`
			fragment IssueHistoryFragment on JiraIssue
			@refetchable(queryName: "IssueHistory_RefetchQuery") {
				history(first: $first, after: $after, last: $last, before: $before, orderBy: $orderBy)
					@connection(key: "IssueHistory_history") {
					edges {
						node {
							id
							i18nDescription
							fieldDisplayName
							fieldId
							fieldType
							timestamp
							fieldSchema {
								type
								customFieldType
							}
							isDueToRedaction
							isDueToRedactionRestore
							from {
								... on JiraHistoryStatusFieldValue {
									formattedValue
									categoryId
									displayValue
									value
								}

								... on JiraHistoryWorkLogFieldValue {
									formattedValue
									displayValue
									value
									worklog {
										id
										worklogId
										timeSpent {
											timeInSeconds
										}
										workDescription {
											adfValue {
												json
												convertedPlainText {
													plainText
												}
											}
										}
									}
								}

								... on JiraHistoryAssigneeFieldValue {
									formattedValue
									value
									avatarUrl
									displayValue
								}

								... on JiraHistoryPriorityFieldValue {
									value
									displayValue
									formattedValue
									iconUrl
									absoluteIconUrl
								}

								... on JiraHistoryRespondersFieldValue {
									formattedValue
									displayValue
									value
									responders {
										ari
										name
										type
										avatarUrl
									}
								}

								... on JiraHistoryGenericFieldValue {
									formattedValue
									displayValue
									value
								}
							}
							to {
								... on JiraHistoryStatusFieldValue {
									formattedValue
									categoryId
									displayValue
									value
								}

								... on JiraHistoryWorkLogFieldValue {
									formattedValue
									displayValue
									value
									worklog {
										id
										worklogId
										timeSpent {
											timeInSeconds
										}
										workDescription {
											adfValue {
												json
												convertedPlainText {
													plainText
												}
											}
										}
									}
								}

								... on JiraHistoryAssigneeFieldValue {
									formattedValue
									value
									avatarUrl
									displayValue
								}

								... on JiraHistoryPriorityFieldValue {
									value
									displayValue
									formattedValue
									iconUrl
									absoluteIconUrl
								}

								... on JiraHistoryRespondersFieldValue {
									formattedValue
									displayValue
									value
									responders {
										ari
										name
										type
										avatarUrl
									}
								}

								... on JiraHistoryGenericFieldValue {
									formattedValue
									displayValue
									value
								}
							}
							actor {
								name
								accountId
								picture
							}
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						endCursor
						startCursor
					}
				}
			}
		`,
		queryData?.jira?.issueByKey,
	);

	// Track query variables to detect genuine query executions
	const executionKey = `${issueKey}-${cloudId}-${orderBy}`;
	// Track the executionKey we've already fired analytics for to prevent duplicates
	const consumedExecutionKeyRef = useRef<string | null>(null);

	// Analytics for FETCH_PAGE - fire once per query execution when data is available
	useEffect(() => {
		if (
			consumedExecutionKeyRef.current !== executionKey &&
			data?.history?.edges &&
			Array.isArray(data.history.edges) &&
			data.history.edges.length > 0
		) {
			onHistoryFetchAnalytics();
			consumedExecutionKeyRef.current = executionKey;
		}
	}, [executionKey, data?.history?.edges, onHistoryFetchAnalytics]);

	const handleLoadMoreNext = useCallback(() => {
		if (hasNext && !isLoadingNext) {
			loadNext(HISTORY_ITEMS_TO_LOAD, {
				onComplete: (error: Error | null) => {
					if (!error) {
						onHistoryFetchAnalytics();
					}
				},
			});
		}
	}, [hasNext, isLoadingNext, loadNext, onHistoryFetchAnalytics]);

	if (
		!data?.history?.edges ||
		(Array.isArray(data.history.edges) && data.history.edges.length === 0)
	) {
		return null;
	}

	// Adapter function to convert Relay data to Gira format for reuse of existing transformation
	const adaptRelayToGiraFormat = (relayItem: HistoryEdgeNode) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const adaptFieldValue = (fieldValue: any) => {
			if (!fieldValue) return null;
			const adapted = { ...fieldValue };
			if (fieldValue.worklog) {
				adapted.worklog = {
					...fieldValue.worklog,
					// comment:
					// 	fieldValue.worklog.workDescription?.adfValue?.convertedPlainText?.plainText || '',
					timeSpent: secondsToTimeString(
						timeTrackingConfig.daysPerWeek,
						timeTrackingConfig.hoursPerDay,
					)(fieldValue.worklog.timeSpent?.timeInSeconds ?? 0),
				};
			}
			return adapted;
		};

		return {
			fieldId: relayItem.fieldId ?? '',
			fieldType: (relayItem.fieldType === 'jira' || relayItem.fieldType === 'custom'
				? relayItem.fieldType
				: 'custom') as 'jira' | 'custom',
			fieldSchema: relayItem.fieldSchema
				? {
						...relayItem.fieldSchema,
						type: relayItem.fieldSchema.type ?? '',
					}
				: undefined,
			timestamp: relayItem.timestamp ?? 0, // Convert null/undefined to 0
			actor: relayItem.actor
				? {
						accountId: relayItem.actor.accountId ?? '',
						avatarUrls: {
							large: relayItem.actor.picture ?? '',
						},
						displayName: relayItem.actor.name ?? '',
					}
				: undefined,
			fieldDisplayName: relayItem.fieldDisplayName ?? '',
			isDueToRedaction: relayItem.isDueToRedaction ?? false,
			isDueToRedactionRestore: relayItem.isDueToRedactionRestore ?? false,
			from: adaptFieldValue(relayItem.from),
			to: adaptFieldValue(relayItem.to),
			timeTrackingConfig,
		};
	};

	// Transform Relay data to HistoryItem[] format using existing transformation logic
	const rawHistoryItems = data.history.edges.map((edge: HistoryEdge) => edge.node);
	const adaptedItems = rawHistoryItems.map(adaptRelayToGiraFormat);
	const transformedItems = adaptedItems.map(transformChangelogItem);
	const groupedItems = groupHistoryItems(transformedItems);

	return (
		<FeedContainer data-testid="issue-history.ui.feed-container">
			<HistoryItemsView
				history={groupedItems}
				hasMore={hasNext}
				isLoadingMore={isLoadingNext}
				onLoadMore={handleLoadMoreNext}
				sortOrder={sortOrder}
				GroupedActivityEntryComponent={GroupedActivityEntryComponent}
			/>
		</FeedContainer>
	);
};

function IssueHistory(props: Props): React.JSX.Element {
	return (
		<Placeholder
			name="issue-history-placeholder"
			fallback={
				<FeedContainer data-testid="issue-history.ui.feed-container">
					<FeedSkeleton />
				</FeedContainer>
			}
		>
			<IssueHistoryRelay {...props} />
		</Placeholder>
	);
}

export default IssueHistory;
